//#region Imports Packages and Libs

import { Util } from "../../util";
import { SendTranscriptParams, TranscriptStatus, ValidationResult } from "../../types/components/transcript";
import ERRORS from "../../scripts/footer-buttons/validation/error_messages";

//#endregion

//#region Transcript Validator Class

/**
 * Class responsible for validating transcript parameters
 */
export class TranscriptValidator {
	//#region Public Methods

	/**
	 * Validates the parameters for sending a transcript
	 * @param {SendTranscriptParams} params - The parameters to validate
	 * @returns {ValidationResult} - Result of the validation
	 */
	public static validate(params: SendTranscriptParams): ValidationResult {
		// Check if params is an object and not null
		if (!params || typeof params !== "object") {
			return this.createErrorResult("error_invalid_request");
		}
		return this.validateEmailParameters(params);
	}

	//#endregion

	//#region Private Methods

	/**
	 * Validates that at least one email address is provided
	 * @param {SendTranscriptParams} params - The parameters to validate
	 * @returns {ValidationResult} - Result of the validation
	 */
	private static validateEmailParameters(params: SendTranscriptParams): ValidationResult {
		const { primaryEmail = "", additionalEmails = [] } = params;

		// Validate either primaryEmail or additionalEmails is provided
		const hasPrimary = Boolean(primaryEmail && Util.trimString(primaryEmail));
		const hasAdditional = Array.isArray(additionalEmails) && additionalEmails.length > 0;

		// At least one of primaryEmail or additionalEmails must be provided
		if (!hasPrimary && !hasAdditional) {
			return this.createErrorResult("error_missing_recipient");
		}

		return {
			isValid: true,
			errorResponse: {
				status: TranscriptStatus.Success,
				status_message: ""
			}
		};
	}

	/**
	 * Creates a standard error result with an i18n error key
	 * @param {string} errorKey - The i18n key for the error message (e.g., "error_invalid_request")
	 * @returns {ValidationResult} - The error result with error key for i18n resolution
	 */
	private static createErrorResult(errorKey: string): ValidationResult {
		return {
			isValid: false,
			errorKey: errorKey,
			errorResponse: {
				status: TranscriptStatus.Fail,
				status_message: errorKey,
			},
		};
	}
}

//#endregion
